import { Component, computed, DestroyRef, OnInit, signal, WritableSignal } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { PaceBarcode } from '@evorait/pace-components';
import { BarcodeScannerService } from '../../services/barcode-scanner/barcode-scanner.service';
import { Observable, Subject, switchMap, throwError, timer } from 'rxjs';
import { TextModel } from '../../models/primitive/text.model';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { TextModelOptions } from '../../models/options/text-model-options';
import { v4 as uuidv4 } from 'uuid';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';
import { faDownload, faSync, faUpload, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { ConfigConstants } from '../../config/config-constants';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const CLEAR_STATUS_INTERVAL = 3000;

@Component({
    selector: 'pace-core-barcode-clipboard-modal',
    templateUrl: './barcode-clipboard-modal.component.html',
    styleUrls: ['./barcode-clipboard-modal.component.scss'],
})
export class BarcodeClipboardModalComponent implements OnInit {
    public isOnline$ = this.connectivityService.isOnline$;
    public barcodes$: Observable<PaceBarcode[]> = this.barcodeScannerService.barcodeClipboard$;
    public barcodeTextModel: TextModel = new TextModel(
        'barcodeScanDummy',
        '',
        new TextModelOptions({ required: true, scannable: true }),
    );
    public labelTextModel: TextModel = new TextModel(
        'barcodeScanDummy',
        '',
        new TextModelOptions({ required: true, scannable: false }),
    );

    public addBarcodeForm: FormGroup;
    public formChildren: FormArray;

    public fetchIcon = faDownload;
    public uploadIcon = faUpload;

    protected readonly faLoading = faSync;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faExclamationTriangle = faExclamationTriangle;

    public clipboardSyncStatus = ConfigConstants.SYNCING_STATUS;

    public uploadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.clipboardSyncStatus.IDLE);
    public downloadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.clipboardSyncStatus.IDLE);
    public loading = computed(
        () =>
            this.uploadStatus() === this.clipboardSyncStatus.LOADING ||
            this.downloadStatus() === this.clipboardSyncStatus.LOADING,
    );

    private resetStatusChangeSubject: Subject<void> = new Subject<void>();

    protected strings: {
        downloadButtonLabel: string;
        uploadButtonLabel: string;
    };

    public constructor(
        private readonly modalInstance: BsModalRef,
        private readonly barcodeScannerService: BarcodeScannerService,
        private readonly connectivityService: ConnectivityService,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly destroyRef: DestroyRef,
    ) {}

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.addBarcodeForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
        this.strings = {
            downloadButtonLabel: <string>this.translate.instant('BARCODE_CLIPBOARD_LABEL_BUTTON_FETCH_CLIPBOARD'),
            uploadButtonLabel: <string>this.translate.instant('BARCODE_CLIPBOARD_LABEL_BUTTON_UPLOAD_CLIPBOARD'),
        };
        this.resetStatusChangeSubject
            .asObservable()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => timer(CLEAR_STATUS_INTERVAL).pipe(take(1))),
                tap(() => {
                    this.uploadStatus.set(this.clipboardSyncStatus.IDLE);
                    this.downloadStatus.set(this.clipboardSyncStatus.IDLE);
                }),
            )
            .subscribe();
    }

    public hideModal(): void {
        this.modalInstance.hide();
    }

    public updateBarcodes(barcodes: PaceBarcode[]): void {
        this.barcodeScannerService.setBarcodeClipboard(barcodes);
    }

    public addBarcode(): void {
        const barcode: PaceBarcode = {
            barcode: this.barcodeTextModel.value,
            label: this.labelTextModel.value,
            timestamp: new Date().toISOString(),
            id: uuidv4(),
        };
        this.barcodeScannerService.barcodeClipboard$.pipe(take(1)).subscribe((barcodes) => {
            this.updateBarcodes([barcode, ...barcodes]);
            this.barcodeTextModel.value = '';
            this.labelTextModel.value = '';
            this.formChildren.reset([]);
            this.addBarcodeForm.reset();
        });
    }

    public fetchBarcodes(): void {
        this.downloadStatus.set(this.clipboardSyncStatus.LOADING);
        this.barcodeScannerService
            .fetchBarcodeClipboard()
            .pipe(
                take(1),
                tap(() => this.downloadStatus.set(this.clipboardSyncStatus.SUCCESS)),
                catchError((error) => {
                    this.downloadStatus.set(this.clipboardSyncStatus.ERROR);
                    return throwError(() => error);
                }),
                finalize(() => this.clearStatus()),
            )
            .subscribe();
    }

    public uploadBarcodes(): void {
        this.uploadStatus.set(this.clipboardSyncStatus.LOADING);
        this.barcodeScannerService.barcodeClipboard$
            .pipe(
                switchMap((barcodes) => this.barcodeScannerService.uploadBarcodeClipboard(barcodes)),
                tap(() => this.uploadStatus.set(this.clipboardSyncStatus.SUCCESS)),
                catchError((error) => {
                    this.uploadStatus.set(this.clipboardSyncStatus.ERROR);
                    return throwError(() => error);
                }),
                finalize(() => this.clearStatus()),
                take(1),
            )
            .subscribe();
    }

    private clearStatus(): void {
        this.resetStatusChangeSubject.next();
    }
}
